// NEWBORN — 0–3 months
// Deep editorial guide. Understanding + foundations. Educational, non-medical.
// Extended resource blocks (recognition / normalVsFoundations / andreaQuestions /
// tonight / whenSupport) are rendered only for guides that define them.

export const newborn = {
  id: "0-3",
  num: "02",
  unit: "0\u20133 months",
  name: "Newborn",
  img: "https://images.unsplash.com/photo-1770831208055-f373ff919753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBjb21mb3J0aW5nJTIwYmFieSUyMGluJTIwY3JpYnxlbnwwfHx8fDE3ODc5Mzk2NjN8MA&ixlib=rb-4.1.0&q=85&w=900",
  alt: "A mother holding her newborn close beside the crib",
  titleLines: [
    { t: "Newborn sleep can feel" },
    { t: "completely unpredictable.", em: true },
  ],
  // Resource identity for the shareable "Newborn Sleep Guide" direct link:
  // the primary semantic title; titleLines then read as the emotional subhead.
  resourceTitle: "Newborn Sleep Guide",
  resourceDescriptor: "Understand what\u2019s normal, what you can begin working on now, and when getting a little support can make things easier.",
  introFragments: [
    "One night you get a decent stretch.",
    "The next, you\u2019re awake every hour.",
    "They sleep beautifully in your arms \u2014 and wake the second they\u2019re put down.",
    "Days and nights seem completely backwards.",
  ],
  introParas: [
    "And somewhere in the middle of all of it \u2014 the feeding, the rocking, the 3 a.m. searching \u2014 you\u2019re trying to work out three questions.",
  ],
  introStrong: [
    { t: "Is this normal?" },
    { t: "Am I supposed to be doing something differently?" },
    { t: "Can we even work on sleep this early?" },
  ],
  introAfter: "You don\u2019t have to figure those out alone.",
  recognition: {
    heading: "What can newborn sleep actually look like?",
    sub: "Most people I talk to recognize several of these at once. If that\u2019s you, you\u2019re looking at a newborn \u2014 not at something you\u2019ve done wrong.",
    items: [
      { t: "Frequent night waking", note: "Waking every couple of hours to feed is the texture of this stage, not a malfunction." },
      { t: "Short sleep stretches", note: "Forty minutes here, two hours there. Newborn sleep comes in fragments before it comes in stretches." },
      { t: "Day/night confusion", note: "Sleepy all day, alert at 2 a.m. The internal clock that separates day from night is still under construction." },
      { t: "Short or inconsistent naps", note: "Naps aren\u2019t a system yet \u2014 they\u2019re a series of events, and their length swings widely in these months." },
      { t: "Contact naps", note: "Some days every nap happens on you. Warmth, heartbeat and movement are the sleep environment your baby knows best." },
      { t: "Sleeping better while held", note: "Your body is familiar; a still, flat bassinet is a big change by comparison. Preferring you is biology, not a bad habit." },
      { t: "Waking at the transfer", note: "Asleep in your arms, eyes open the moment they touch the mattress. The put-down changes every condition at once." },
      { t: "Unpredictable settling", note: "The thing that worked last night does nothing tonight. Settling is inconsistent before it\u2019s reliable." },
      { t: "Feeding and sleep tangled together", note: "At this age the two are almost the same system \u2014 hunger, comfort and sleep blur into each other." },
      { t: "Changing week to week", note: "Just as a pattern appears, it shifts. Development is moving fast enough that \u2018normal\u2019 keeps being redefined." },
    ],
  },
  normalVsFoundations: {
    heading: "What\u2019s normal \u2014 and what can we work on?",
    lead: "Newborn sleep is developmentally different from older-baby sleep. Some of what you\u2019re seeing is simply the stage. Some of it is groundwork we can gently shape. Telling those two apart is most of the job.",
    normalLabel: "Normal at this age",
    normal: [
      "Waking often to feed, around the clock",
      "Short stretches and irregular naps",
      "Needing a lot of help to fall asleep",
      "Sleep that looks different from one day to the next",
      "A day/night rhythm that hasn\u2019t finished developing",
    ],
    foundationsLabel: "Foundations we can begin",
    foundations: [
      "Helping days and nights start to look different",
      "Looking at the rhythm of feeds, awake time and sleep",
      "Building simple, repeatable sleep cues",
      "Learning your baby\u2019s early tired signals",
      "Noticing whether timing is making settling harder",
      "Tuning the sleep environment",
      "Understanding why the same part of the day keeps falling apart",
      "Setting expectations that fit this stage",
    ],
    outro: "None of this is sleep training. There\u2019s no schedule to enforce and no independence to demand from a newborn \u2014 it\u2019s understanding, plus a few gentle structures that make the coming months easier.",
  },
  andreaQuestions: {
    heading: "What I would want to know about your baby",
    lead: "If we talked, I wouldn\u2019t start by handing you a schedule. I\u2019d start by listening. These are the questions I\u2019d ask first:",
    questions: [
      "What do your nights actually look like, hour by hour?",
      "When does your baby seem most settled?",
      "When does everything start falling apart?",
      "Where are they sleeping \u2014 and how are naps going?",
      "What happens when you try to put them down?",
      "What have you already tried?",
      "And what are you most worried about?",
    ],
    outro: "Your answers tell me far more than any chart. They show me the baby you actually have \u2014 so we work from what\u2019s really happening, instead of forcing your newborn into a generic plan.",
  },
  tonight: {
    heading: "Try this tonight",
    lead: "Nothing to fix tonight \u2014 just a few things worth noticing. Observation first; change later.",
    items: [
      { t: "Notice when the longest stretch naturally happens", note: "Whatever time it occurs, that window tells you when your baby\u2019s sleep is currently deepest." },
      { t: "Watch for early tired signs", note: "Turning away, glazing over, going quiet, jerky movements \u2014 the cues that show up before the crying." },
      { t: "Clock the awake time before a hard settle", note: "If settling falls apart after a long stretch awake, timing may be doing more than technique." },
      { t: "Keep nights low-key", note: "Dim light, quiet voices, minimal interaction during night feeds \u2014 calm where you can manage it." },
      { t: "Notice if the same difficulty repeats at the same time", note: "A hard patch that arrives on schedule is a pattern \u2014 and patterns are workable." },
    ],
    note: "And the one non-negotiable: the sleep space stays flat, clear and on their back \u2014 however the night is going. If anything about feeding, breathing or health is worrying you, that\u2019s a pediatrician conversation first.",
  },
  whenSupport: {
    heading: "When support can help",
    paras: [
      "\u201cIs my baby too young for a sleep consultant?\u201d I hear that often. The honest answer: newborn support looks nothing like the sleep training most people picture.",
      "Working with a newborn sleep consultant isn\u2019t about expecting your six-week-old to sleep like a six-month-old. Nothing gets forced, and nothing about this stage needs to be \u2018trained\u2019 away.",
    ],
    points: [
      "Understanding what\u2019s developmentally realistic for this exact stage",
      "Identifying the patterns hiding inside the chaos",
      "Establishing healthy foundations early",
      "Reducing the trial-and-error that wears parents down",
      "Knowing what to work on now \u2014 and what can wait",
      "Feeling more confident about what you\u2019re seeing",
    ],
    closing: "Whether you\u2019re in Denver or further out \u2014 if you\u2019d like experienced eyes on what you\u2019re seeing, that\u2019s exactly what the complimentary assessment is for.",
  },
  topics: [
    {
      id: "normal-newborn-sleep",
      q: "What does normal newborn sleep look like?",
      body: [
        "More variable than almost anyone tells you. In the first three months, sleep is typically fragmented into short stretches, distributed across day and night, and organized around feeding. Frequent waking is a feature of this stage, not evidence of failure.",
        "Two things make this stage feel chaotic. First, babies differ — sleep temperament, feeding patterns and settling style vary widely from one newborn to the next. Second, the same baby varies: a day that looks organized can be followed by one that looks like nothing has a pattern at all.",
        "Underneath the noise, a circadian rhythm is slowly developing, and sleep gradually consolidates. But \u2018gradually\u2019 is the honest word. Knowing the range of normal keeps you from measuring a healthy newborn against a schedule written for an older baby.",
      ],
    },
    {
      id: "only-sleeps-when-held",
      q: "Why does my newborn only sleep when held?",
      body: [
        "Because your arms are the environment your newborn knows best. Warmth, movement, heartbeat, scent, closeness — after nine months of being continuously held, your body is the most familiar sleep surface there is. A still, flat bassinet is a dramatic change by comparison.",
        "The transfer itself also matters. Moving from arms to mattress changes temperature, pressure and sensation all at once, and a newborn\u2019s sleep is light enough that the change often wakes them. None of this means you\u2019ve created a \u2018bad habit\u2019. It means your baby is behaving like a newborn.",
        "The useful question isn\u2019t \u2018how do I stop this\u2019 — it\u2019s \u2018what exactly happens when\u2019. The pattern of the waking tells you more than the waking itself.",
      ],
      observe: [
        "Does your baby wake during the movement of the transfer itself?",
        "Immediately after being placed down — or several minutes later?",
        "Only during certain sleep periods, like the first stretch of the night?",
        "Does anything about the sleep surface environment change the outcome?",
      ],
    },
    {
      id: "wakes-when-put-down",
      q: "Why does my newborn wake the second I put them down?",
      body: [
        "If you\u2019ve lived it, you know the choreography. They\u2019re asleep. You wait. You stand up carefully. You lower them slowly. Pause. Remove one hand. Then the other. Eyes open.",
        "What you\u2019re seeing is how significant that transition is for a newborn. Falling asleep is a state your baby reached under one set of conditions — held, warm, slightly moving. The put-down removes all of them at once, and light newborn sleep surfaces easily under that kind of change.",
        "This is also why the put-down is information, not just frustration. A baby who wakes the moment conditions change is telling you something about how they got to sleep — a theme that becomes central in the months ahead. For now, it\u2019s worth observing rather than fighting. And always: the safe sleep surface is the flat, clear one — however tempting the alternatives feel at 3 a.m.",
      ],
    },
    {
      id: "awake-at-night",
      q: "Why is my newborn awake at night?",
      body: [
        "What parents call \u2018day/night confusion\u2019 is usually just an unfinished clock. Newborns aren\u2019t born with a mature circadian rhythm — the internal system that separates day from night develops across the early months.",
        "While it develops, the environment can teach quietly. Days that look like days — ordinary light, ordinary noise, ordinary life — and nights that look like nights: dim, calm, feeds handled with minimal fanfare and straight back to sleep.",
        "The expectation to hold: these cues support a process that\u2019s already underway. They don\u2019t flip a switch. If nights feel upside-down right now, that\u2019s a stage with a direction, not a life sentence.",
      ],
    },
    {
      id: "newborn-schedule",
      q: "Should my newborn have a schedule?",
      body: [
        "Not in the clock sense — and chasing one usually creates more anxiety than sleep. Newborn days are built from feeding, brief awake windows, sleep cues, settling and naps that refuse to hold fixed times.",
        "What works instead is a pattern: a recognizable order of events (feed, a little awake time, wind-down, sleep) anchored to your baby\u2019s cues rather than the clock. Bedtime routines fit the same logic — a short, repeatable sequence that signals sleep, not a guarantee of it.",
        "Watch for what\u2019s becoming consistent over a week, not what happened today. One unusual day is weather. A repeated shape is climate.",
      ],
    },
    {
      id: "unpredictable-naps",
      q: "Why are my newborn\u2019s naps so unpredictable?",
      body: [
        "Because newborn naps aren\u2019t yet a system — they\u2019re a series of events. Nap length and timing in the first months swing widely: forty minutes here, two hours there, contact naps some days, bassinet naps others.",
        "A single short nap is almost never evidence that something went wrong. Even a string of them can be ordinary at this age. What\u2019s worth noticing is the overall texture: roughly how much daytime sleep is happening, how your baby behaves between sleeps, and whether the general shape is drifting anywhere over the weeks.",
        "If naps only happen in arms right now, that\u2019s common — and it\u2019s a separate question from whether it needs to change.",
      ],
    },
    {
      id: "contact-naps",
      q: "What about contact naps?",
      body: [
        "The internet has made contact naps weirdly stressful, so let\u2019s separate two conversations. If you love the contact nap and it works for your family — that\u2019s not a problem. That\u2019s a preference, and it\u2019s yours to make.",
        "It becomes a different conversation when the baby can only sleep on someone and the family needs that to change — when you\u2019re nap-trapped daily and running out of margin. Then the question is how to gently widen the ways your baby can sleep, at a pace that fits this stage.",
        "Either way, the safety boundary is fixed: contact sleep happens with an awake, attentive caregiver. A sleeping adult and a sleeping baby on a couch or chair is the scenario safe-sleep guidance warns about — not the snuggle itself.",
      ],
    },
    {
      id: "bedtime-routine",
      q: "What does a newborn bedtime routine actually look like?",
      body: [
        "Simpler than the internet suggests. You\u2019re not building a ritual — you\u2019re building a signal. Lower stimulation, feed as appropriate, diaper, dim environment, a few quiet minutes, the same short settling sequence, sleep.",
        "The power is in repetition, not elaborateness. A baby who experiences the same cues in the same order begins to recognize what comes next. Over time that recognition does real work.",
        "What a routine won\u2019t do is guarantee a particular night. Some nights the routine ends in sleep; some nights it ends in round two. Both are normal at this age — the routine is an investment that pays later.",
      ],
    },
    {
      id: "what-were-working-on",
      q: "What are we actually working on at this age?",
      body: [
        "The honest list: understanding your baby\u2019s patterns, recognizing developing rhythms, keeping sleep cues repeatable, holding realistic expectations, and maintaining safe sleep. And just as much — understanding what you cannot control yet.",
        "None of this is about forcing a newborn to sleep through the night. Newborn sleep support is education and gentle structure: reading the baby in front of you, and knowing which questions are worth watching and which are just this stage being this stage.",
        "That\u2019s also where an outside set of experienced eyes can help — not to train a newborn, but to tell you what\u2019s ordinary, what\u2019s worth tracking, and what can wait.",
      ],
      strong: ["Foundations before", "expectations."],
    },
    {
      id: "pediatrician",
      q: "When should this be a pediatrician conversation?",
      body: [
        "Whenever the concern is health. Feeding that isn\u2019t going well, slow weight gain, breathing that concerns you, unusual lethargy, illness, or anything that registers as medically wrong — those go to your child\u2019s healthcare professional, full stop.",
        "Sleep support handles the other side: patterns, routines, expectations and settling once health is established. The two work together, and a good rule is simple — health first, then sleep.",
      ],
    },
  ],
  checklist: {
    title: ["My newborn…"],
    items: [
      "mostly sleeps while being held",
      "wakes when transferred",
      "seems awake much of the night",
      "has very short naps",
      "is difficult to settle",
      "has no predictable rhythm",
      "suddenly changed how they sleep",
      "sleeps differently every day",
      "has me wondering what is actually normal",
      "has me questioning whether I should be doing more",
    ],
    after: [
      { t: "The checklist doesn\u2019t" },
      { t: "diagnose the problem." },
      { t: "It helps us ask", em: true },
      { t: "better questions.", em: true },
    ],
  },
  ending: {
    lines: [
      { t: "Not sure whether what you\u2019re seeing" },
      { t: "is normal \u2014 or whether there\u2019s" },
      { t: "something we could make easier?", em: true },
    ],
    sub: "Tell me what\u2019s happening \u2014 what last night looked like, what\u2019s been hardest, and what you\u2019ve already tried. I\u2019ll help you understand what I\u2019m hearing, and what I would look at first.",
  },
  deepLink: { href: "/newborn-sleep-consultant", label: "Learn more about newborn sleep" },
  ctas: [
    { type: "tell", label: "Tell Andrea what\u2019s happening" },
    { type: "book", label: "Book my complimentary sleep assessment" },
    { type: "text", label: "Text Andrea" },
  ],
};
